import { useState, useEffect, useMemo } from 'react';
import { API_URL } from '../config';
import { Search, Package, Warehouse, Filter, RefreshCw, History, Download, X, AlertTriangle, TrendingUp, LayoutGrid, List, MoreHorizontal, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTableSort } from '../hooks/useTableSort';
import SortableTh from '../components/SortableTh';

export default function StockPage() {
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('stock'); // 'stock' | 'movimientos'
    
    // Data
    const [stock, setStock] = useState([]);
    const [sucursales, setSucursales] = useState([]);
    const [bodegas, setBodegas] = useState([]);
    const [bodegasDestino, setBodegasDestino] = useState([]);
    const [proveedores, setProveedores] = useState([]);
    const [, setSummary] = useState([]);
    
    // Filters
    const [filters, setFilters] = useState({
        sucursal_id: '',
        bodega_id: '',
        proveedor_id: ''
    });
    const [, setProveedorQuery] = useState('');
    const [stockSort, setStockSort] = useState('nombre'); // 'nombre' | 'mas' | 'menos'
    const [bodegaView, setBodegaView] = useState('cards');
    const [productQuery, setProductQuery] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [selectedRows, setSelectedRows] = useState([]);
    const [rowMenu, setRowMenu] = useState(null);

    // Kardex / movimientos por producto
    const [movFilters, setMovFilters] = useState({
        search: '',
        sucursal_id: '',
        desde: '',
        hasta: '',
    });
    const [movimientos, setMovimientos] = useState([]);
    const [movProducto, setMovProducto] = useState(null);
    const [movLoading, setMovLoading] = useState(false);
    const [movError, setMovError] = useState('');
    const [movProductSuggestions, setMovProductSuggestions] = useState([]);
    const [showMovProductList, setShowMovProductList] = useState(false);
    const [movProductSearching, setMovProductSearching] = useState(false);
    const [selectedMovProductoId, setSelectedMovProductoId] = useState(null);
    
    // Modals
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [showTransferModal, setShowTransferModal] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    
    // Form data
    const [updateData, setUpdateData] = useState({
        producto_id: '',
        sucursal_id: '',
        bodega_id: '',
        cantidad: 0,
        motivo: '',
        proveedor_id: ''
    });
    
    const [transferData, setTransferData] = useState({
        producto_id: '',
        sucursal_origen: '',
        bodega_origen: '',
        sucursal_destino: '',
        bodega_destino: '',
        cantidad: 0,
        motivo: ''
    });

    useEffect(() => {
        fetchSucursales();
        fetchProveedores();
        fetchSummary();
    }, []);

    useEffect(() => {
        if (filters.sucursal_id) {
            fetchBodegas(filters.sucursal_id);
        } else {
            setBodegas([]);
        }
    }, [filters.sucursal_id]);

    useEffect(() => {
        if (transferData.sucursal_destino) {
            fetchBodegasForTransfer(transferData.sucursal_destino);
        } else {
            setBodegasDestino([]);
        }
    }, [transferData.sucursal_destino]);

    useEffect(() => {
        fetchStock();
    }, [filters]);

    useEffect(() => {
        if (activeTab !== 'movimientos') return;
        const q = movFilters.search.trim();
        if (selectedMovProductoId || q.length < 1) {
            if (q.length < 1) setMovProductSuggestions([]);
            return undefined;
        }
        let cancelled = false;
        setMovProductSearching(true);
        const timeoutId = setTimeout(async () => {
            try {
                const res = await fetch(
                    `${API_URL}/api/stock/productos?search=${encodeURIComponent(q)}`,
                    { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
                );
                const data = await res.json().catch(() => []);
                if (cancelled) return;
                setMovProductSuggestions(Array.isArray(data) ? data : []);
                setShowMovProductList(true);
            } catch (err) {
                console.error('Error searching productos:', err);
                if (!cancelled) setMovProductSuggestions([]);
            } finally {
                if (!cancelled) setMovProductSearching(false);
            }
        }, 250);
        return () => {
            cancelled = true;
            clearTimeout(timeoutId);
        };
    }, [movFilters.search, activeTab, selectedMovProductoId]);

    const fetchSucursales = async () => {
        try {
            const res = await fetch(`${API_URL}/api/sucursales`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (res.ok) {
                const data = await res.json();
                setSucursales(data.filter(s => s.estado !== false));
            }
        } catch (err) {
            console.error('Error fetching sucursales:', err);
        }
    };

    const fetchProveedores = async () => {
        try {
            const res = await fetch(`${API_URL}/api/providers`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (res.ok) {
                const data = await res.json();
                setProveedores(Array.isArray(data) ? data : []);
            }
        } catch (err) {
            console.error('Error fetching proveedores:', err);
        }
    };

    const fetchBodegas = async (sucursalId) => {
        try {
            const res = await fetch(`${API_URL}/api/bodegas/sucursal/${sucursalId}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (res.ok) {
                setBodegas(await res.json());
            }
        } catch (err) {
            console.error('Error fetching bodegas:', err);
        }
    };

    const fetchBodegasForTransfer = async (sucursalId) => {
        try {
            const res = await fetch(`${API_URL}/api/bodegas/sucursal/${sucursalId}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (res.ok) {
                setBodegasDestino(await res.json());
            }
        } catch (err) {
            console.error('Error fetching bodegas for transfer:', err);
        }
    };

    const fetchStock = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filters.sucursal_id) params.append('sucursal_id', filters.sucursal_id);
            if (filters.bodega_id) params.append('bodega_id', filters.bodega_id);
            if (filters.proveedor_id) params.append('proveedor_id', filters.proveedor_id);
            if (stockSort === 'mas') params.append('sort', 'mas_stock');
            if (stockSort === 'menos') params.append('sort', 'menos_stock');

            const res = await fetch(`${API_URL}/api/stock?${params}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (res.ok) {
                setStock(await res.json());
            }
        } catch (err) {
            console.error('Error fetching stock:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchSummary = async () => {
        try {
            const params = filters.sucursal_id ? `?sucursal_id=${filters.sucursal_id}` : '';
            const res = await fetch(`${API_URL}/api/stock/summary${params}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (res.ok) {
                setSummary(await res.json());
            }
        } catch (err) {
            console.error('Error fetching summary:', err);
        }
    };

    const loadKardex = async (productoId) => {
        if (!productoId) {
            setMovError('Escriba y seleccione un producto de la lista.');
            setShowMovProductList(true);
            return;
        }
        setMovLoading(true);
        setMovError('');
        setShowMovProductList(false);
        try {
            const params = new URLSearchParams();
            params.set('producto_id', String(productoId));
            if (movFilters.sucursal_id) params.set('sucursal_id', movFilters.sucursal_id);
            if (movFilters.desde) params.set('desde', movFilters.desde);
            if (movFilters.hasta) params.set('hasta', movFilters.hasta);
            params.set('limit', '2000');

            const res = await fetch(`${API_URL}/api/stock/movimientos?${params}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setMovimientos([]);
                setMovProducto(null);
                setMovError(data.error || 'No se pudo cargar el reporte');
                return;
            }
            setMovProducto(data.producto || null);
            setMovimientos(data.movimientos || []);
            if (!data.producto) {
                setMovError('No se encontró un producto con ese criterio.');
            } else if (!(data.movimientos || []).length) {
                setMovError('Sin movimientos registrados para este producto en el rango seleccionado.');
            }
        } catch (err) {
            console.error(err);
            setMovError('Error de red al consultar movimientos.');
            setMovimientos([]);
            setMovProducto(null);
        } finally {
            setMovLoading(false);
        }
    };

    const selectMovProducto = (product) => {
        setSelectedMovProductoId(product.id);
        setMovFilters((prev) => ({ ...prev, search: product.nombre || '' }));
        setShowMovProductList(false);
        setMovProductSuggestions([]);
        loadKardex(product.id);
    };

    const fetchMovimientos = async (e) => {
        e?.preventDefault?.();
        if (selectedMovProductoId) {
            await loadKardex(selectedMovProductoId);
            return;
        }
        const q = movFilters.search.trim();
        if (!q) {
            setMovError('Escriba el nombre o código del producto.');
            return;
        }
        let suggestions = movProductSuggestions;
        if (!suggestions.length) {
            try {
                const res = await fetch(
                    `${API_URL}/api/stock/productos?search=${encodeURIComponent(q)}`,
                    { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
                );
                const data = await res.json().catch(() => []);
                suggestions = Array.isArray(data) ? data : [];
                setMovProductSuggestions(suggestions);
            } catch (err) {
                console.error(err);
            }
        }
        if (suggestions.length === 1) {
            selectMovProducto(suggestions[0]);
            return;
        }
        if (suggestions.length > 1) {
            setShowMovProductList(true);
            setMovError('Seleccione un producto de la lista.');
            return;
        }
        setMovError('No se encontró un producto con ese criterio.');
    };

    const exportMovimientosCsv = () => {
        if (!movimientos.length) return;
        const headers = [
            'Producto',
            'Stock Inicial',
            'Stock Modificado',
            'Sucursal',
            'Motivo',
            'Glosa',
            'Usuario',
            'Fecha',
        ];
        const lines = [headers.join(';')];
        for (const m of movimientos) {
            const fecha = m.fecha
                ? new Date(m.fecha).toLocaleString('es-CL', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                  })
                : '';
            lines.push(
                [
                    m.producto_nombre,
                    m.stock_inicial,
                    m.stock_modificado,
                    m.sucursal_nombre,
                    m.motivo,
                    m.glosa,
                    m.usuario,
                    fecha,
                ]
                    .map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`)
                    .join(';')
            );
        }
        const blob = new Blob(['\ufeff' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `movimientos_${(movProducto?.nombre || 'producto').replace(/\s+/g, '_')}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const formatMovFecha = (fecha) => {
        if (!fecha) return '—';
        return new Date(fecha).toLocaleString('es-CL', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const openKardexForProduct = async (item) => {
        const search = item.producto_nombre || item.codigo_barra || '';
        setActiveTab('movimientos');
        setSelectedMovProductoId(item.producto_id);
        setMovFilters((prev) => ({ ...prev, search }));
        setShowMovProductList(false);
        await loadKardex(item.producto_id);
    };

    const handleUpdateStock = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                ...updateData,
                proveedor_id: updateData.proveedor_id === '' || updateData.proveedor_id == null
                    ? null
                    : Number(updateData.proveedor_id)
            };
            const res = await fetch(`${API_URL}/api/stock`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(payload)
            });

            if (res.ok) {
                alert('Stock actualizado correctamente');
                setShowUpdateModal(false);
                setUpdateData({ producto_id: '', sucursal_id: '', bodega_id: '', cantidad: 0, motivo: '', proveedor_id: '' });
                fetchStock();
                fetchSummary();
            } else {
                const error = await res.json();
                alert(`Error: ${error.error}`);
            }
        } catch (err) {
            console.error('Error updating stock:', err);
            alert('Error al actualizar stock');
        }
    };

    const handleTransferStock = async (e) => {
        e.preventDefault();
        try {
            const res = await fetch(`${API_URL}/api/stock/transfer`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(transferData)
            });

            if (res.ok) {
                alert('Transferencia realizada correctamente');
                setShowTransferModal(false);
                setTransferData({
                    producto_id: '',
                    sucursal_origen: '',
                    bodega_origen: '',
                    sucursal_destino: '',
                    bodega_destino: '',
                    cantidad: 0,
                    motivo: ''
                });
                fetchStock();
                fetchSummary();
            } else {
                const error = await res.json();
                alert(`Error: ${error.error}`);
            }
        } catch (err) {
            console.error('Error transferring stock:', err);
            alert('Error al transferir stock');
        }
    };

    const openUpdateModal = (product) => {
        setSelectedProduct(product);
        setUpdateData({
            producto_id: product.producto_id,
            sucursal_id: product.sucursal_id,
            bodega_id: product.bodega_id,
            cantidad: product.cantidad,
            motivo: '',
            proveedor_id: product.proveedor_id_efectivo || product.proveedor_id || product.ultimo_proveedor_id || ''
        });
        setShowUpdateModal(true);
    };

    const openTransferModal = (product) => {
        setSelectedProduct(product);
        setTransferData({
            producto_id: product.producto_id,
            sucursal_origen: product.sucursal_id,
            bodega_origen: product.bodega_id,
            sucursal_destino: '',
            bodega_destino: '',
            cantidad: 0,
            motivo: ''
        });
        setShowTransferModal(true);
    };

    const canStock = (code) => !Array.isArray(user?.permissions) || user.permissions.includes(code);

    const stockTone = (item) => {
        const qty = Number(item.cantidad) || 0;
        const min = Number(item.stock_minimo) || 0;
        if (qty <= 0) return 'critico';
        if (min > 0 && qty <= min) return 'bajo';
        return 'normal';
    };

    const bodegaCards = useMemo(() => {
        const map = new Map();
        stock.forEach((item) => {
            const key = `${item.sucursal_id}_${item.bodega_id}`;
            if (!map.has(key)) {
                map.set(key, {
                    key,
                    sucursal_id: item.sucursal_id,
                    sucursal_nombre: item.sucursal_nombre || 'Sin sucursal',
                    bodega_id: item.bodega_id,
                    bodega_nombre: item.bodega_nombre || 'Sin bodega',
                    productos: 0,
                    stockTotal: 0,
                    sinStock: 0,
                    bajoMinimo: 0,
                });
            }
            const card = map.get(key);
            const qty = Number(item.cantidad) || 0;
            const min = Number(item.stock_minimo) || 0;
            card.productos += 1;
            card.stockTotal += qty;
            if (qty <= 0) card.sinStock += 1;
            else if (min > 0 && qty <= min) card.bajoMinimo += 1;
        });
        return [...map.values()].sort((a, b) => b.stockTotal - a.stockTotal);
    }, [stock]);

    const kpis = useMemo(() => {
        const sucursalesSet = new Set(stock.map((item) => item.sucursal_id).filter((id) => id != null));
        const broken = new Set(
            stock.filter((item) => (Number(item.cantidad) || 0) <= 0).map((item) => item.sucursal_id)
        );
        const bajo = stock.filter((item) => stockTone(item) === 'bajo').length;
        const total = stock.length;
        return {
            products: total,
            stockTotal: stock.reduce((sum, item) => sum + (Number(item.cantidad) || 0), 0),
            sucursales: sucursalesSet.size,
            broken: broken.size,
            bajo,
            bajoPct: total ? (bajo / total) * 100 : 0,
        };
    }, [stock]);

    const bodegaTone = (card) => {
        if (!card.productos || card.stockTotal <= 0 || card.sinStock / card.productos >= 0.5) return 'critico';
        if (card.sinStock > 0 || card.bajoMinimo > 0) return 'atencion';
        return 'operativa';
    };

    const searchedStock = useMemo(() => {
        const q = productQuery.trim().toLowerCase();
        if (!q) return stock;
        return stock.filter((item) =>
            (item.producto_nombre || '').toLowerCase().includes(q)
            || String(item.codigo_barra || '').toLowerCase().includes(q)
        );
    }, [stock, productQuery]);

    const { sorted: displayedStock, thProps: stockThProps, setSort: setStockColSort } = useTableSort(searchedStock, {
        defaultKey: 'producto_nombre',
        getters: {
            cantidad: (item) => Number(item.cantidad) || 0,
            stock_minimo: (item) => Number(item.stock_minimo) || 0,
            estado: (item) => {
                if (item.cantidad <= 0) return 0;
                if (item.cantidad <= item.stock_minimo) return 1;
                return 2;
            },
        },
    });

    const { sorted: sortedMovimientos, thProps: movThProps } = useTableSort(movimientos, {
        defaultKey: 'fecha',
        defaultDir: 'desc',
        getters: {
            stock_inicial: (m) => Number(m.stock_inicial) || 0,
            stock_modificado: (m) => Number(m.stock_modificado) || 0,
        },
    });

    const pageCount = Math.max(1, Math.ceil(displayedStock.length / pageSize));
    const safePage = Math.min(page, pageCount);
    const pageRows = displayedStock.slice((safePage - 1) * pageSize, safePage * pageSize);
    const pageFrom = displayedStock.length === 0 ? 0 : (safePage - 1) * pageSize + 1;
    const pageTo = Math.min(safePage * pageSize, displayedStock.length);
    const pageKeys = pageRows.map((item) => `${item.producto_id}_${item.sucursal_id}_${item.bodega_id}`);
    const allPageSelected = pageKeys.length > 0 && pageKeys.every((key) => selectedRows.includes(key));

    const exportStockCsv = () => {
        const rows = selectedRows.length
            ? displayedStock.filter((item) => selectedRows.includes(`${item.producto_id}_${item.sucursal_id}_${item.bodega_id}`))
            : displayedStock;
        const lines = ['Producto,SKU,Sucursal,Bodega,Stock,Minimo,Estado'];
        rows.forEach((item) => {
            const tone = stockTone(item);
            const estado = tone === 'critico' ? 'Critico' : tone === 'bajo' ? 'Bajo minimo' : 'Normal';
            lines.push([
                `"${(item.producto_nombre || '').replace(/"/g, '""')}"`,
                item.codigo_barra || '',
                `"${(item.sucursal_nombre || '').replace(/"/g, '""')}"`,
                `"${(item.bodega_nombre || '').replace(/"/g, '""')}"`,
                item.cantidad ?? 0,
                item.stock_minimo ?? 0,
                estado,
            ].join(','));
        });
        const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'stock.csv';
        link.click();
        URL.revokeObjectURL(url);
    };

    const clearStockFilters = () => {
        setFilters({ sucursal_id: '', bodega_id: '', proveedor_id: '' });
        setProveedorQuery('');
        setStockSort('nombre');
        setProductQuery('');
        setPage(1);
        setStockColSort('producto_nombre', 'asc');
    };

    const formatMovShort = (value) => {
        if (!value) return '—';
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return '—';
        return date.toLocaleString('es-CL', {
            day: 'numeric',
            month: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hourCycle: 'h23',
            timeZone: 'America/Santiago',
        });
    };

    return (
        <div className="stock-screen">
            <div className="stock-head">
                <div className="stock-title">
                    <span className="stock-title-icon" aria-hidden><Package size={22} /></span>
                    <div>
                        <h1>Gestión de Stock</h1>
                        <p>Administre el stock por sucursal y bodega · Kardex por producto</p>
                    </div>
                </div>
                <div className="stock-head-side">
                    <aside className="stock-quote">
                        <p>“Un buen control de stock impulsa grandes negocios”</p>
                        <span aria-hidden><Warehouse size={18} /></span>
                    </aside>
                    <button
                        type="button"
                        className="stock-refresh"
                        onClick={() => {
                            if (activeTab === 'movimientos') fetchMovimientos();
                            else { fetchStock(); fetchSummary(); }
                        }}
                    >
                        <RefreshCw size={16} /> Actualizar
                    </button>
                </div>
            </div>

            <div className="stock-tabs">
                <button type="button" className={activeTab === 'stock' ? 'is-on' : ''} onClick={() => setActiveTab('stock')}>
                    <Package size={15} /> Stock actual
                </button>
                <button type="button" className={activeTab === 'movimientos' ? 'is-on' : ''} onClick={() => setActiveTab('movimientos')}>
                    <History size={15} /> Movimientos (Kardex)
                </button>
            </div>

            {activeTab === 'movimientos' && (
                <>
                    <form
                        onSubmit={fetchMovimientos}
                        className="bg-white p-4 rounded-lg shadow border border-gray-200 mb-6"
                    >
                        <div className="flex items-center gap-2 mb-4">
                            <Filter size={20} className="text-gray-500" />
                            <h3 className="font-semibold">Reporte de movimiento por producto</h3>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium mb-1" htmlFor="kardex-producto-search">
                                    Producto (nombre o código)
                                </label>
                                <div className="relative">
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={16} aria-hidden />
                                        <input
                                            id="kardex-producto-search"
                                            type="text"
                                            className="w-full pl-10 pr-10 py-2 border rounded"
                                            placeholder="Escriba para buscar… (ej: coca)"
                                            value={movFilters.search}
                                            onChange={(e) => {
                                                setMovFilters({ ...movFilters, search: e.target.value });
                                                setSelectedMovProductoId(null);
                                                setShowMovProductList(true);
                                                setMovError('');
                                            }}
                                            onFocus={() => {
                                                if (movProductSuggestions.length > 0 && !selectedMovProductoId) {
                                                    setShowMovProductList(true);
                                                }
                                            }}
                                            onBlur={() => setTimeout(() => setShowMovProductList(false), 180)}
                                            autoComplete="off"
                                        />
                                        {movFilters.search && (
                                            <button
                                                type="button"
                                                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-700"
                                                title="Limpiar búsqueda"
                                                aria-label="Limpiar búsqueda"
                                                onMouseDown={(e) => e.preventDefault()}
                                                onClick={() => {
                                                    setMovFilters({ ...movFilters, search: '' });
                                                    setSelectedMovProductoId(null);
                                                    setMovProductSuggestions([]);
                                                    setMovimientos([]);
                                                    setMovProducto(null);
                                                    setMovError('');
                                                }}
                                            >
                                                <X size={16} />
                                            </button>
                                        )}
                                    </div>
                                    {showMovProductList && movFilters.search.trim().length >= 1 && !selectedMovProductoId && (
                                        <ul className="absolute z-30 mt-1 w-full max-h-64 overflow-y-auto bg-white border rounded-lg shadow-lg">
                                            {movProductSearching && (
                                                <li className="px-3 py-2 text-sm text-gray-500">Buscando…</li>
                                            )}
                                            {!movProductSearching && movProductSuggestions.map((p) => (
                                                <li key={p.id}>
                                                    <button
                                                        type="button"
                                                        className="w-full text-left px-3 py-2 text-sm hover:bg-amber-50"
                                                        onMouseDown={(e) => e.preventDefault()}
                                                        onClick={() => selectMovProducto(p)}
                                                    >
                                                        <span className="font-medium">{p.nombre}</span>
                                                        {p.codigo_barra ? (
                                                            <span className="block text-xs text-gray-500 font-mono">{p.codigo_barra}</span>
                                                        ) : null}
                                                    </button>
                                                </li>
                                            ))}
                                            {!movProductSearching && movProductSuggestions.length === 0 && (
                                                <li className="px-3 py-2 text-sm text-gray-500">No hay productos con ese nombre o código</li>
                                            )}
                                        </ul>
                                    )}
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                    Escriba y elija el producto de la lista.
                                </p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Sucursal</label>
                                <select
                                    className="w-full p-2 border rounded"
                                    value={movFilters.sucursal_id}
                                    onChange={(e) => setMovFilters({ ...movFilters, sucursal_id: e.target.value })}
                                >
                                    <option value="">Todas</option>
                                    {sucursales.map((s) => (
                                        <option key={s.id || s.sucursal_id} value={s.id || s.sucursal_id}>
                                            {s.nombre}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Desde</label>
                                <input
                                    type="date"
                                    className="w-full p-2 border rounded"
                                    value={movFilters.desde}
                                    onChange={(e) => setMovFilters({ ...movFilters, desde: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Hasta</label>
                                <input
                                    type="date"
                                    className="w-full p-2 border rounded"
                                    value={movFilters.hasta}
                                    onChange={(e) => setMovFilters({ ...movFilters, hasta: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-4">
                            <button
                                type="submit"
                                disabled={movLoading}
                                className="px-4 py-2 rounded bg-indigo-600 text-white font-semibold hover:bg-indigo-700 disabled:opacity-50"
                            >
                                {movLoading ? 'Buscando…' : 'Buscar'}
                            </button>
                            <button
                                type="button"
                                disabled={!movimientos.length}
                                onClick={exportMovimientosCsv}
                                className="px-4 py-2 rounded border border-gray-300 bg-white font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2"
                            >
                                <Download size={16} /> Exportar CSV
                            </button>
                        </div>
                        {movProducto && (
                            <p className="mt-3 text-sm text-gray-600">
                                Producto:{' '}
                                <strong>{movProducto.nombre}</strong>
                                {movProducto.codigo_barra ? (
                                    <span className="text-gray-400"> · {movProducto.codigo_barra}</span>
                                ) : null}
                                {' · '}
                                {movimientos.length} movimiento(s)
                            </p>
                        )}
                        {movError && <p className="mt-2 text-sm text-amber-700">{movError}</p>}
                    </form>

                    <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
                        {movLoading ? (
                            <div className="p-8 text-center text-gray-500">Cargando movimientos…</div>
                        ) : movimientos.length === 0 ? (
                            <div className="p-8 text-center text-gray-500">
                                <History size={48} className="mx-auto mb-4 text-gray-300" />
                                <p>Busque un producto para ver su kardex de movimientos.</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-50 border-b">
                                        <tr>
                                            <SortableTh columnKey="producto_nombre" {...movThProps} className="p-3 text-left text-sm font-semibold">Producto</SortableTh>
                                            <SortableTh columnKey="stock_inicial" {...movThProps} className="p-3 text-right text-sm font-semibold" align="right">Stock Inicial</SortableTh>
                                            <SortableTh columnKey="stock_modificado" {...movThProps} className="p-3 text-right text-sm font-semibold" align="right">Stock Modificado</SortableTh>
                                            <SortableTh columnKey="sucursal_nombre" {...movThProps} className="p-3 text-left text-sm font-semibold">Sucursal</SortableTh>
                                            <SortableTh columnKey="motivo" {...movThProps} className="p-3 text-left text-sm font-semibold">Motivo</SortableTh>
                                            <SortableTh columnKey="glosa" {...movThProps} className="p-3 text-left text-sm font-semibold">Glosa</SortableTh>
                                            <SortableTh columnKey="usuario" {...movThProps} className="p-3 text-left text-sm font-semibold">Usuario</SortableTh>
                                            <SortableTh columnKey="fecha" {...movThProps} className="p-3 text-left text-sm font-semibold">Fecha</SortableTh>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {sortedMovimientos.map((m) => (
                                            <tr key={m.id} className="hover:bg-gray-50">
                                                <td className="p-3 font-medium">{m.producto_nombre}</td>
                                                <td className="p-3 text-right font-mono">
                                                    {Number(m.stock_inicial ?? 0).toFixed(2)}
                                                </td>
                                                <td className="p-3 text-right font-mono">
                                                    {Number(m.stock_modificado ?? 0).toFixed(2)}
                                                </td>
                                                <td className="p-3">{m.sucursal_nombre}</td>
                                                <td className="p-3">
                                                    <span className="px-2 py-0.5 rounded text-xs font-bold bg-slate-100 text-slate-700">
                                                        {m.motivo}
                                                    </span>
                                                </td>
                                                <td className="p-3 text-sm text-gray-600">{m.glosa}</td>
                                                <td className="p-3 text-sm">{m.usuario}</td>
                                                <td className="p-3 text-sm whitespace-nowrap">{formatMovFecha(m.fecha)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </>
            )}

            {activeTab === 'stock' && (
            <>
            <section className="stock-kpis">
                <article className="stock-kpi">
                    <span className="stock-kpi-icon is-gold"><Package size={18} /></span>
                    <div>
                        <p>Total de productos</p>
                        <strong>{kpis.products.toLocaleString('es-CL')}</strong>
                    </div>
                </article>
                <article className="stock-kpi">
                    <span className="stock-kpi-icon is-green"><TrendingUp size={18} /></span>
                    <div>
                        <p>Stock total</p>
                        <strong>{kpis.stockTotal.toLocaleString('es-CL')}</strong>
                    </div>
                </article>
                <article className="stock-kpi">
                    <span className="stock-kpi-icon is-rose"><AlertTriangle size={18} /></span>
                    <div>
                        <p>Sucursales con quiebre</p>
                        <strong>{kpis.broken.toLocaleString('es-CL')}</strong>
                        <small>de {kpis.sucursales.toLocaleString('es-CL')} sucursales</small>
                    </div>
                </article>
                <article className="stock-kpi">
                    <span className="stock-kpi-icon is-amber"><AlertTriangle size={18} /></span>
                    <div>
                        <p>Productos bajo mínimo</p>
                        <strong>{kpis.bajo.toLocaleString('es-CL')}</strong>
                        <small>{kpis.bajoPct.toLocaleString('es-CL', { maximumFractionDigits: 1 })}% del total</small>
                    </div>
                </article>
            </section>

            <section className="stock-panel">
                <header className="stock-panel-head">
                    <div>
                        <h2>Stock por sucursal y bodega</h2>
                        <p>Resumen de inventario en tiempo real</p>
                    </div>
                    <div className="stock-view-toggle">
                        <button type="button" className={bodegaView === 'cards' ? 'is-on' : ''} onClick={() => setBodegaView('cards')}>
                            <LayoutGrid size={15} /> Vista de tarjetas
                        </button>
                        <button type="button" className={bodegaView === 'table' ? 'is-on' : ''} onClick={() => setBodegaView('table')}>
                            <List size={15} /> Vista de tabla
                        </button>
                    </div>
                </header>
                {bodegaCards.length === 0 ? (
                    <p className="stock-empty">No hay bodegas con stock para estos filtros.</p>
                ) : bodegaView === 'cards' ? (
                    <div className="stock-bodegas">
                        {bodegaCards.map((card) => {
                            const tone = bodegaTone(card);
                            const label = tone === 'critico' ? 'Crítico' : tone === 'atencion' ? 'Atención' : 'Operativa';
                            return (
                                <button
                                    key={card.key}
                                    type="button"
                                    className="stock-bodega"
                                    onClick={() => {
                                        setFilters({ ...filters, sucursal_id: String(card.sucursal_id || ''), bodega_id: String(card.bodega_id || '') });
                                        setPage(1);
                                    }}
                                >
                                    <div className="stock-bodega-top">
                                        <span className="stock-kpi-icon is-gold"><Warehouse size={16} /></span>
                                        <span>
                                            <strong>{card.sucursal_nombre}</strong>
                                            <small>{card.bodega_nombre}</small>
                                        </span>
                                        <em className={`is-${tone}`}>{label}</em>
                                    </div>
                                    <div className="stock-bodega-nums">
                                        <div><b>{card.productos.toLocaleString('es-CL')}</b><span>Productos</span></div>
                                        <div><b>{card.stockTotal.toLocaleString('es-CL')}</b><span>Stock total</span></div>
                                    </div>
                                    <div className="stock-bodega-pills">
                                        <span className="is-rose">{card.sinStock.toLocaleString('es-CL')} Sin stock</span>
                                        <span className="is-amber">{card.bajoMinimo.toLocaleString('es-CL')} Bajo mínimo</span>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                ) : (
                    <div className="stock-table-wrap">
                        <table className="stock-table">
                            <thead>
                                <tr>
                                    <th>Sucursal</th>
                                    <th>Bodega</th>
                                    <th>Productos</th>
                                    <th>Stock total</th>
                                    <th>Sin stock</th>
                                    <th>Bajo mínimo</th>
                                    <th>Estado</th>
                                </tr>
                            </thead>
                            <tbody>
                                {bodegaCards.map((card) => {
                                    const tone = bodegaTone(card);
                                    const label = tone === 'critico' ? 'Crítico' : tone === 'atencion' ? 'Atención' : 'Operativa';
                                    return (
                                        <tr key={card.key}>
                                            <td>{card.sucursal_nombre}</td>
                                            <td>{card.bodega_nombre}</td>
                                            <td>{card.productos.toLocaleString('es-CL')}</td>
                                            <td>{card.stockTotal.toLocaleString('es-CL')}</td>
                                            <td>{card.sinStock.toLocaleString('es-CL')}</td>
                                            <td>{card.bajoMinimo.toLocaleString('es-CL')}</td>
                                            <td><span className={`stock-pill is-${tone}`}>{label}</span></td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            <section className="stock-panel">
                <header className="stock-panel-head">
                    <h2><Filter size={16} /> Filtros</h2>
                    <div className="stock-filter-actions">
                        <button type="button" className="stock-filter-btn" onClick={() => { fetchStock(); fetchSummary(); setPage(1); }}>
                            <Filter size={15} /> Filtrar
                        </button>
                        <button type="button" className="stock-clear" onClick={clearStockFilters}>
                            <X size={15} /> Limpiar
                        </button>
                        <button type="button" className="stock-clear" onClick={exportStockCsv}>
                            <Download size={15} /> Exportar
                        </button>
                    </div>
                </header>
                <div className="stock-filters">
                    <label>
                        Sucursal
                        <select
                            value={filters.sucursal_id}
                            onChange={(e) => {
                                setFilters({ ...filters, sucursal_id: e.target.value, bodega_id: '' });
                                setPage(1);
                            }}
                        >
                            <option value="">Todas las sucursales</option>
                            {sucursales.map((s) => (
                                <option key={s.id || s.sucursal_id} value={s.id || s.sucursal_id}>{s.nombre}</option>
                            ))}
                        </select>
                    </label>
                    <label>
                        Bodega
                        <select
                            value={filters.bodega_id}
                            onChange={(e) => { setFilters({ ...filters, bodega_id: e.target.value }); setPage(1); }}
                            disabled={!filters.sucursal_id}
                        >
                            <option value="">Todas las bodegas</option>
                            {bodegas.map((b) => (
                                <option key={b.id || b.bodega_id} value={b.id || b.bodega_id}>{b.nombre}</option>
                            ))}
                        </select>
                    </label>
                    <label>
                        Ordenar por stock
                        <select
                            value={stockSort}
                            onChange={(e) => {
                                const v = e.target.value;
                                setStockSort(v);
                                setPage(1);
                                if (v === 'mas') setStockColSort('cantidad', 'desc');
                                else if (v === 'menos') setStockColSort('cantidad', 'asc');
                                else setStockColSort('producto_nombre', 'asc');
                            }}
                        >
                            <option value="nombre">Por nombre</option>
                            <option value="mas">Más stock primero</option>
                            <option value="menos">Menos stock primero</option>
                        </select>
                    </label>
                    <label>
                        Proveedor
                        <select
                            value={filters.proveedor_id}
                            onChange={(e) => {
                                const id = e.target.value;
                                const found = proveedores.find((p) => String(p.proveedor_id) === id);
                                setProveedorQuery(found?.nombre || '');
                                setFilters({ ...filters, proveedor_id: id });
                                setPage(1);
                            }}
                        >
                            <option value="">Todos los proveedores</option>
                            {proveedores.map((p) => (
                                <option key={p.proveedor_id} value={p.proveedor_id}>{p.nombre}</option>
                            ))}
                        </select>
                    </label>
                    <label className="stock-search">
                        <Search size={15} aria-hidden />
                        <input
                            value={productQuery}
                            onChange={(e) => { setProductQuery(e.target.value); setPage(1); }}
                            placeholder="Buscar producto, SKU o nombre..."
                        />
                    </label>
                </div>
            </section>

            <section className="stock-panel">
                <header className="stock-panel-head">
                    <h2>Lista de productos <small>Mostrando {pageRows.length.toLocaleString('es-CL')} de {displayedStock.length.toLocaleString('es-CL')} productos</small></h2>
                </header>
                {loading ? (
                    <p className="stock-empty">Cargando stock...</p>
                ) : pageRows.length === 0 ? (
                    <p className="stock-empty">No se encontró stock con los filtros seleccionados.</p>
                ) : (
                    <div className="stock-table-wrap">
                        <table className="stock-table">
                            <thead>
                                <tr>
                                    <th>
                                        <input
                                            type="checkbox"
                                            checked={allPageSelected}
                                            onChange={() => {
                                                setSelectedRows((prev) => (
                                                    allPageSelected
                                                        ? prev.filter((key) => !pageKeys.includes(key))
                                                        : [...new Set([...prev, ...pageKeys])]
                                                ));
                                            }}
                                            aria-label="Seleccionar página"
                                        />
                                    </th>
                                    <SortableTh columnKey="producto_nombre" {...stockThProps}>Producto</SortableTh>
                                    <th>SKU / Código</th>
                                    <SortableTh columnKey="sucursal_nombre" {...stockThProps}>Sucursal</SortableTh>
                                    <SortableTh columnKey="bodega_nombre" {...stockThProps}>Bodega</SortableTh>
                                    <SortableTh columnKey="cantidad" {...stockThProps}>Stock actual</SortableTh>
                                    <SortableTh columnKey="stock_minimo" {...stockThProps}>Stock mínimo</SortableTh>
                                    <SortableTh columnKey="estado" {...stockThProps}>Estado</SortableTh>
                                    <th>Último movimiento</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pageRows.map((item) => {
                                    const key = `${item.producto_id}_${item.sucursal_id}_${item.bodega_id}`;
                                    const tone = stockTone(item);
                                    const label = tone === 'critico' ? 'Crítico' : tone === 'bajo' ? 'Bajo mínimo' : 'Normal';
                                    return (
                                        <tr key={key}>
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedRows.includes(key)}
                                                    onChange={() => setSelectedRows((prev) => prev.includes(key) ? prev.filter((id) => id !== key) : [...prev, key])}
                                                    aria-label={`Seleccionar ${item.producto_nombre}`}
                                                />
                                            </td>
                                            <td>
                                                <div className="stock-product">
                                                    {item.foto_url ? <img src={item.foto_url} alt="" /> : <span />}
                                                    <strong>{item.producto_nombre}</strong>
                                                </div>
                                            </td>
                                            <td className="stock-sku">{item.codigo_barra || '—'}</td>
                                            <td>{item.sucursal_nombre || '—'}</td>
                                            <td>{item.bodega_nombre || '—'}</td>
                                            <td className={`stock-qty is-${tone}`}>{Number(item.cantidad || 0).toLocaleString('es-CL')}</td>
                                            <td>{Number(item.stock_minimo || 0).toLocaleString('es-CL')}</td>
                                            <td><span className={`stock-pill is-${tone}`}>{label}</span></td>
                                            <td>{formatMovShort(item.ultimo_movimiento || item.updated_at)}</td>
                                            <td>
                                                <div className="stock-actions">
                                                    <button type="button" onClick={() => openKardexForProduct(item)}>Ver</button>
                                                    <div className="stock-more">
                                                        <button type="button" title="Más" onClick={() => setRowMenu(rowMenu === key ? null : key)}>
                                                            <MoreHorizontal size={16} />
                                                        </button>
                                                        {rowMenu === key && (
                                                            <div className="stock-more-menu">
                                                                {canStock('stock_editar') && (
                                                                    <button type="button" onClick={() => { setRowMenu(null); openUpdateModal(item); }}>Ajustar stock</button>
                                                                )}
                                                                {canStock('stock_transferir') && (
                                                                    <button type="button" onClick={() => { setRowMenu(null); openTransferModal(item); }}>Transferir</button>
                                                                )}
                                                                <button type="button" onClick={() => { setRowMenu(null); openKardexForProduct(item); }}>Ver kardex</button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
                <footer className="stock-pager">
                    <span>Mostrando {pageFrom.toLocaleString('es-CL')} a {pageTo.toLocaleString('es-CL')} de {displayedStock.length.toLocaleString('es-CL')}</span>
                    <div>
                        <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage === 1} aria-label="Página anterior"><ChevronLeft size={16} /></button>
                        {Array.from({ length: pageCount }, (_, index) => index + 1).slice(0, 6).map((n) => (
                            <button key={n} type="button" className={n === safePage ? 'is-current' : ''} onClick={() => setPage(n)}>{n}</button>
                        ))}
                        <button type="button" onClick={() => setPage((p) => Math.min(pageCount, p + 1))} disabled={safePage === pageCount} aria-label="Página siguiente"><ChevronRight size={16} /></button>
                        <label>
                            Mostrar
                            <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}>
                                {[10, 20, 50].map((size) => <option key={size} value={size}>{size}</option>)}
                            </select>
                            por página
                        </label>
                    </div>
                </footer>
            </section>
            </>
            )}

            {/* Update Stock Modal */}
            {showUpdateModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] flex flex-col shadow-xl">
                        <h3 className="text-xl font-bold px-6 pt-5 pb-3 shrink-0 border-b">Ajustar Stock</h3>
                        <form onSubmit={handleUpdateStock} className="flex flex-col min-h-0 flex-1">
                            <div className="space-y-4 overflow-y-auto px-6 py-4 flex-1 min-h-0">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Producto</label>
                                    <input
                                        type="text"
                                        className="w-full p-2 border rounded bg-gray-50"
                                        value={selectedProduct?.producto_nombre || ''}
                                        disabled
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Sucursal</label>
                                        <input
                                            type="text"
                                            className="w-full p-2 border rounded bg-gray-50"
                                            value={selectedProduct?.sucursal_nombre || ''}
                                            disabled
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Bodega</label>
                                        <input
                                            type="text"
                                            className="w-full p-2 border rounded bg-gray-50"
                                            value={selectedProduct?.bodega_nombre || ''}
                                            disabled
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Cantidad</label>
                                    <input
                                        type="number"
                                        className="w-full p-2 border rounded"
                                        value={updateData.cantidad}
                                        onChange={(e) => setUpdateData({ ...updateData, cantidad: parseInt(e.target.value) || 0 })}
                                        min="0"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Proveedor</label>
                                    <select
                                        className="w-full p-2 border rounded bg-white"
                                        value={updateData.proveedor_id}
                                        onChange={(e) => setUpdateData({ ...updateData, proveedor_id: e.target.value })}
                                    >
                                        <option value="">Sin asignar</option>
                                        {proveedores.map((p) => (
                                            <option key={p.proveedor_id} value={p.proveedor_id}>
                                                {p.nombre}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Motivo (opcional)</label>
                                    <textarea
                                        className="w-full p-2 border rounded"
                                        rows="3"
                                        value={updateData.motivo}
                                        onChange={(e) => setUpdateData({ ...updateData, motivo: e.target.value })}
                                        placeholder="Ej: Ajuste por inventario físico"
                                    />
                                </div>
                            </div>
                            <div className="flex gap-2 px-6 py-4 border-t bg-white shrink-0">
                                <button
                                    type="button"
                                    onClick={() => setShowUpdateModal(false)}
                                    className="flex-1 btn-secondary min-h-[44px]"
                                >
                                    Cancelar
                                </button>
                                <button type="submit" className="flex-1 btn-primary min-h-[44px]">
                                    Guardar
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Transfer Stock Modal */}
            {showTransferModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] flex flex-col shadow-xl">
                        <h3 className="text-xl font-bold px-6 pt-5 pb-3 shrink-0 border-b">Transferir Stock</h3>
                        <form onSubmit={handleTransferStock} className="flex flex-col min-h-0 flex-1">
                            <div className="space-y-4 overflow-y-auto px-6 py-4 flex-1 min-h-0">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Producto</label>
                                    <input
                                        type="text"
                                        className="w-full p-2 border rounded bg-gray-50"
                                        value={selectedProduct?.producto_nombre || ''}
                                        disabled
                                    />
                                </div>
                                <div className="bg-gray-50 p-3 rounded">
                                    <div className="text-sm font-semibold mb-2">Origen</div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Sucursal</label>
                                            <input
                                                type="text"
                                                className="w-full p-2 border rounded bg-white text-sm"
                                                value={selectedProduct?.sucursal_nombre || ''}
                                                disabled
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Bodega</label>
                                            <input
                                                type="text"
                                                className="w-full p-2 border rounded bg-white text-sm"
                                                value={selectedProduct?.bodega_nombre || ''}
                                                disabled
                                            />
                                        </div>
                                    </div>
                                    <div className="mt-2 text-xs text-gray-600">
                                        Stock disponible: <span className="font-bold">{selectedProduct?.cantidad || 0}</span>
                                    </div>
                                </div>
                                <div className="bg-blue-50 p-3 rounded">
                                    <div className="text-sm font-semibold mb-2">Destino</div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Sucursal</label>
                                            <select
                                                className="w-full p-2 border rounded text-sm"
                                                value={transferData.sucursal_destino}
                                                onChange={(e) => setTransferData({ ...transferData, sucursal_destino: e.target.value, bodega_destino: '' })}
                                                required
                                            >
                                                <option value="">Seleccionar...</option>
                                                {sucursales.filter(s => (s.id || s.sucursal_id) != transferData.sucursal_origen).map(s => (
                                                    <option key={s.id || s.sucursal_id} value={s.id || s.sucursal_id}>
                                                        {s.nombre}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Bodega</label>
                                            <select
                                                className="w-full p-2 border rounded text-sm"
                                                value={transferData.bodega_destino}
                                                onChange={(e) => setTransferData({ ...transferData, bodega_destino: e.target.value })}
                                                disabled={!transferData.sucursal_destino}
                                                required
                                            >
                                                <option value="">Seleccionar...</option>
                                                {bodegasDestino.map(b => (
                                                    <option key={b.id || b.bodega_id} value={b.id || b.bodega_id}>
                                                        {b.nombre}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Cantidad a Transferir</label>
                                    <input
                                        type="number"
                                        className="w-full p-2 border rounded"
                                        value={transferData.cantidad}
                                        onChange={(e) => setTransferData({ ...transferData, cantidad: parseInt(e.target.value) || 0 })}
                                        min="1"
                                        max={selectedProduct?.cantidad || 0}
                                        required
                                    />
                                    <div className="text-xs text-gray-500 mt-1">
                                        Máximo disponible: {selectedProduct?.cantidad || 0}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Motivo (opcional)</label>
                                    <textarea
                                        className="w-full p-2 border rounded"
                                        rows="2"
                                        value={transferData.motivo}
                                        onChange={(e) => setTransferData({ ...transferData, motivo: e.target.value })}
                                        placeholder="Ej: Transferencia entre sucursales"
                                    />
                                </div>
                            </div>
                            <div className="flex gap-2 px-6 py-4 border-t bg-white shrink-0">
                                <button
                                    type="button"
                                    onClick={() => setShowTransferModal(false)}
                                    className="flex-1 btn-secondary min-h-[44px]"
                                >
                                    Cancelar
                                </button>
                                <button 
                                    type="submit" 
                                    className="flex-1 btn-primary min-h-[44px]"
                                    disabled={!transferData.sucursal_destino || !transferData.bodega_destino || transferData.cantidad <= 0}
                                >
                                    Transferir
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
